import type { GridLayer, SymbolLandEffect, SymbolView } from "@slot/presentation";

/**
 * The per-symbol land animation on a reel stop, and the hand-over of the landed drum to the grid.
 *
 * Every landed view becomes the grid's cell as it is when the last reel settles — still landing or
 * not — so the landing rolls on into the symbol's idle on the same view, and the render replaces
 * only a cell whose symbol differs from the result (user, 2026-09-26, decision 20). Until then a
 * cell still landing was held out of the grid and swapped for a fresh view when its clip ended: for
 * the clip's 0.4 s the grid saw an empty cell, so a tumble in that window skipped the symbol and the
 * late swap painted the old one over the new board (review findings 44, 48), and the swap threw
 * away the idle the clip had queued.
 */
export class ReelLandAnimations {
  /** Landed views the grid owns now; releasing the drum must skip them. */
  private readonly adopted = new Set<SymbolView>();

  public constructor(
    private readonly grid: GridLayer,
    private readonly landEffect: SymbolLandEffect | null,
  ) {}

  /** A fresh spin: any landing still running goes back to rest, the previous hand-over is forgotten. */
  public beginSpin(): void {
    this.landEffect?.finishAll();
    this.adopted.clear();
  }

  /** The land animation for every just-landed view of a column. */
  public playForColumn(column: number, views: readonly (SymbolView | null)[]): void {
    if (!this.landEffect) return;
    views.forEach((view, row) => {
      const symbolId = view?.getSymbolId() ?? "";
      if (!view || !symbolId) return;
      // The content node squashes, not the cell root: the drum still drives the root during the land bounce.
      this.landEffect!.play({ symbolId, cellNode: view.contentNode ?? view.node, symbolSprite: view.symbolSprite, index: this.grid.indexOf(row, column) });
    });
  }

  /** Before the all-stop render: every landed view becomes the grid's cell as it is. */
  public adoptLanded(landedViews: (column: number) => readonly (SymbolView | null)[]): void {
    this.adopted.clear();
    for (let column = 0; column < this.grid.gridColumnCount; column++) {
      const views = landedViews(column);
      for (let row = 0; row < views.length && row < this.grid.gridRowCount; row++) {
        const view = views[row];
        if (!view || view.node.destroyed) continue;
        this.grid.adopt(row * this.grid.gridColumnCount + column, view);
        this.adopted.add(view);
      }
    }
  }

  /** A view the drum must not release: the grid owns it. */
  public owns(view: SymbolView): boolean {
    return this.adopted.has(view);
  }

  /** Recovery: every landing back to rest. */
  public reset(): void {
    this.landEffect?.finishAll();
  }
}
